import { useTranslations } from 'next-intl';
import { toast } from 'react-toastify';

import { ApiErrorResponse } from '@cineamo/api/types/ApiErrorResponse.types';

import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { SeatError } from '@cineamo/ui/src/seat-map/typedefs';

import { useCartProvider } from '@cineamo/composables/src/providers/cartProvider';

import { reportBookingError } from './booking-telemetry-client';

export type SeatMapError = {
    seatId?: number;
    errorKey?: string;
    errorMessage?: string;
};

export function useErrorHandler() {
    const { setErrors } = useCartProvider();
    const t = useTranslations('tickets');

    function handleErrorResponse(errorResponse: ApiErrorResponse<unknown>) {
        // Seat conflicts and validation are the buyer's to fix; no answer and a 5xx are ours (CIN-15436).
        if (!errorResponse.status || errorResponse.status >= 500) {
            reportBookingError({
                step: 'seat-lock-sync',
                code: errorResponse.status ? 'server-error' : 'no-response',
                error: errorResponse
            });
        }
        const bulkErrors = errorResponse.data?.parameters as Record<string, { seat?: Record<keyof SeatError, string> }>;
        const seatErrors: SeatMapError[] = [];
        if (bulkErrors && Object.entries(bulkErrors).length > 0) {
            Object.entries(bulkErrors).forEach(([, error]) => {
                Object.entries(error).forEach(([, value]) => {
                    Object.entries(value).forEach(([key, message]) => {
                        const match = message.match(/\[(\d+)]/);
                        let seatId: number | undefined;
                        if (match) {
                            const id = match[1];
                            seatId = parseInt(id, 10);
                        }
                        seatErrors.push({ seatId: seatId, errorKey: key, errorMessage: message });
                    });
                });
            });
            if (seatErrors.some((seatError) => seatError.errorKey === 'activeSeatLockFound')) {
                showToast({
                    toastType: toast.info,
                    messageTitle: t('error.active-seat-lock-found')
                });
            } else {
                seatErrors.forEach((seatError) => {
                    // API keys are camelCase (noPriceOption), translation keys kebab-case (no-price-option)
                    const errorKey = `error.${seatError.errorKey?.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`)}`;
                    const errorMessage = t.has(errorKey) ? t(errorKey) : null;
                    showToast({
                        toastType: toast.info,
                        messageTitle: errorMessage || t('error.something-went-wrong')
                    });
                });
            }
            setErrors(seatErrors);
        } else {
            showToast({
                toastType: toast.info,
                messageTitle: errorResponse.data?.detail || t('error.something-went-wrong')
            });
        }
    }

    return {
        handleErrorResponse
    };
}
